import type { ReactNode } from 'react';

import { cn } from '../lib/utils';

export interface StatusViewProps {
  /** Large display code, typically "404" or "500". Ignored when `icon` is set. */
  code?: string;
  /**
   * Glyph shown in place of `code`, for screens where no status number applies
   * (maintenance, coming soon). Size and opacity are the caller's to set.
   */
  icon?: ReactNode;
  /** Page heading. */
  title: string;
  /** One-line description. */
  description: string;
  /** Optional action element (link or button). Rendered below the description. */
  action?: ReactNode;
  /** Optional secondary action — usually a less prominent link (e.g. "Report this"). */
  secondaryAction?: ReactNode;
  /** Merged into the root — mainly to override the default `min-h-[60vh]`. */
  className?: string;
}

/**
 * Routing-agnostic status page presentation (404 / 500 / maintenance, etc).
 * Consumers pass their own framework-specific action element (Next.js Link,
 * react-router-dom Link, or a plain anchor). This component handles layout,
 * typography, and brand consistency only.
 */
export function StatusView({
  code,
  icon,
  title,
  description,
  action,
  secondaryAction,
  className,
}: StatusViewProps) {
  return (
    <div
      className={cn(
        'flex min-h-[60vh] flex-col items-center justify-center px-6 text-center',
        className,
      )}
    >
      {icon ?? (
        <span className="font-mono text-7xl font-light tabular-nums opacity-10">
          {code}
        </span>
      )}
      <h2 className="mt-4 font-display text-display-sm font-light leading-[1.1]">
        {title}
      </h2>
      <p className="mt-3 max-w-sm text-sm opacity-60">{description}</p>
      {action && <div className="mt-8">{action}</div>}
      {secondaryAction && (
        <div className="mt-4 text-xs opacity-60">{secondaryAction}</div>
      )}
    </div>
  );
}
